// The €10-off survey's questions and the rules for moving through them.
// `value` is what lands on the Klaviyo profile; edit labels freely, but a
// value change is a data change (segment TZDbgL and the trend reports read
// them). Hub ledger 2026-10-01-092 is the spec, shared with SOLDR and FLOSSR.
//
// Designed from GLORB's strategy (§4.6–4.8) + the team data-collection doc:
//  Q1 attribution (the only clean channel signal under headless / weak EU
//     tracking), Q2 use-case/JTBD (gamer vs design vs gift), Q3 the forced-
//     choice value driver. Q2 and Q3 are unchanged by 092.

// "Was it an ad?" under the platforms that run ads. Before this, 79% of the
// answers were Instagram or Facebook and none said whether it was an ad.
const paid = (organicLabel) => [
  { value: "ad", label: "An ad" },
  { value: "organic", label: organicLabel },
  { value: "unsure", label: "Not sure" },
];

export const PAID_VALUES = ["ad", "organic", "unsure"];

export const HDYHAU = {
  key: "hdyhau",
  title: "Where did you first hear about GLORB?",
  followUpTitle: "Was it an ad?",
  options: [
    {
      value: "instagram",
      label: "Instagram",
      group: "platform",
      followUp: paid("A post or video"),
    },
    {
      value: "facebook",
      label: "Facebook",
      group: "platform",
      followUp: paid("A post or video"),
    },
    {
      value: "youtube",
      label: "YouTube",
      group: "platform",
      followUp: paid("A video"),
    },
    {
      value: "tiktok",
      label: "TikTok",
      group: "platform",
      followUp: paid("A video"),
    },
    { value: "creator", label: "Creator / influencer" },
    {
      value: "google",
      label: "Google search",
      group: "platform",
      followUp: paid("A normal result"),
    },
    { value: "ai_assistant", label: "ChatGPT / AI assistant" },
    { value: "article", label: "Article, blog or podcast" },
    { value: "in_person", label: "Event or in person" },
    { value: "friend", label: "Friend / word of mouth" },
    { value: "reddit", label: "Reddit" },
    { value: "other", label: "Other" },
  ],
};

export const CONTEXT = {
  key: "use_context",
  title: "Where will GLORB mainly live?",
  options: [
    { value: "living_room", label: "Living room / lounge" },
    { value: "bedroom", label: "Bedroom" },
    { value: "home_office", label: "Home office / desk" },
    { value: "gaming_setup", label: "Gaming setup" },
    { value: "studio", label: "Studio / creative space" },
    { value: "kids_room", label: "Kids' room" },
    { value: "parties", label: "Parties / festivals" },
    { value: "other", label: "Other" },
  ],
};

// Q3 forces a single choice among competing VALUES so it actually discriminates
// buyer types (aesthetic vs experiential vs identity vs tech vs price) instead
// of collecting vanity praise. "Price/the deal" is the strategic canary.
export const VALUE = {
  key: "value_driver",
  title: "What matters most to you about GLORB?",
  options: [
    { value: "design", label: "Its design & how it fits my style" },
    { value: "mood", label: "The mood & atmosphere it creates" },
    { value: "novelty", label: "It's a cool, one-of-a-kind object" },
    { value: "creative", label: "Making my own effects & animations" },
    { value: "smart_home", label: "That it fits my smart home (Hue, Matter…)" },
    { value: "price", label: "The price / the deal" },
    { value: "other", label: "Other" },
  ],
};

function shuffle(list, rand) {
  const out = [...list];
  for (let i = out.length - 1; i > 0; i -= 1) {
    const j = Math.floor(rand() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
}

/**
 * Answer order with the first-option bias shuffled out: the platform group
 * first, then the rest, each shuffled on its own; "Other" always last.
 * Call it in the browser only, so SSR and hydration never disagree.
 */
export function orderOptions(options, rand = Math.random) {
  const other = options.filter((o) => o.value === "other");
  const rest = options.filter((o) => o.value !== "other");
  const platforms = rest.filter((o) => o.group === "platform");
  const others = rest.filter((o) => o.group !== "platform");
  return [...shuffle(platforms, rand), ...shuffle(others, rand), ...other];
}

const typed = (text) => (text || "").trim().length >= 2;

export const HINTS = {
  pick: "Pick one to continue.",
  // Not printed: the modal only outlines the "Was it an ad?" panel, whose own
  // question already says what to do. Kept so the outline has a hint to key on.
  followup: "Pick one to continue.",
  other: "Type at least 2 characters to continue.",
};

/** A single-select step with an "Other" free-text answer. */
function singleMissing(value, otherText) {
  if (!value) return "pick";
  if (value === "other" && !typed(otherText)) return "other";
  return null;
}

/**
 * What still stops this step's Next: "pick", "followup", "other", or null
 * when the step is complete. State fields: hdyhau, hdyhauPaid, hdyhauOther,
 * useContext, useContextOther, valueDriver, valueDriverOther.
 */
export function missingPart(step, s) {
  if (step === 1) {
    const base = singleMissing(s.hdyhau, s.hdyhauOther);
    if (base) return base;
    const opt = HDYHAU.options.find((o) => o.value === s.hdyhau);
    return opt?.followUp && !s.hdyhauPaid ? "followup" : null;
  }
  if (step === 2) return singleMissing(s.useContext, s.useContextOther);
  if (step === 3) return singleMissing(s.valueDriver, s.valueDriverOther);
  return null;
}

/** The answer a `signup_10_step` event carries. Never the free text. */
export function stepAnswer(step, s) {
  if (step === 1) {
    const opt = HDYHAU.options.find((o) => o.value === s.hdyhau);
    const out = { question: HDYHAU.key, answer: s.hdyhau };
    if (opt?.followUp && s.hdyhauPaid) out.paid = s.hdyhauPaid;
    return out;
  }
  if (step === 2) return { question: CONTEXT.key, answer: s.useContext };
  return { question: VALUE.key, answer: s.valueDriver };
}

/** Values whose "was it an ad?" answer the server keeps. */
export const PAID_PLATFORMS = HDYHAU.options
  .filter((o) => o.followUp)
  .map((o) => o.value);
